import { AI_GUARD_DEFAULTS, USD_HUF_STALE_DAYS, usdHufStale, FEATURES, PLANS, addMonths, daysBetween, featureAvailable, formatMoney, fromMinor, inTrial, parsePricing, toMinor, todayIso, type Plan, type Pricing } from "@szerviz/core";
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { OperatorStats, smsCell } from "./OperatorStats";
import { OperatorWorkshop } from "./OperatorWorkshop";
import { AiReport } from "./AiReport";
import { SUGGESTED_TAGS, aiCostAlert, invoiceOf, seatText, oddUse, subscriptionTotals } from "./operatorShared";
import { useApp } from "../context";
import { BILLING_STATUSES, OP_BULK_ACTIONS, type AiSettings, type BillingStatus, type OpActivity, type OpAiLimit, type OpAiUsage, type OpBulkAction, type OpSubscription, type OpWorkshop } from "../data";

export { aiCostAlert, oddUse } from "./operatorShared";
type TFunction = ReturnType<typeof useTranslation>["t"];

/**
 * Operator console of Garagevo (VMS-KER Kft.): every workshop with its package,
 * modules, subscription and SMS usage. Only platform admins see it; the database
 * checks it again and logs every change. The list takes bulk actions on the ticked rows.
 */
export function Operator({ openId }: { openId?: string }) {
  const { t, i18n } = useTranslation();
  const { store, go } = useApp();
  const [rows, setRows] = useState<OpWorkshop[] | null>(null);
  const [ai, setAi] = useState<Map<string, OpAiUsage>>(new Map());
  const [aiSettings, setAiSettings] = useState<AiSettings | null>(null);
  const [activity, setActivity] = useState<Map<string, OpActivity>>(new Map());
  const [aiLimits, setAiLimits] = useState<Map<string, OpAiLimit>>(new Map());
  const [subs, setSubs] = useState<Map<string, OpSubscription>>(new Map());
  const [pricing, setPricing] = useState<Pricing | null>(null);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const money = (minor: number) => formatMoney(minor, "HUF", i18n.language);
  const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : "–");

  const load = useCallback(async () => {
    try {
      setRows(await store.opWorkshops());
      setAi(new Map((await store.opAiUsage()).map((u) => [u.workshopId, u])));
      setAiSettings(await store.opAiSettings());
      setActivity(new Map((await store.opActivity()).map((a) => [a.workshopId, a])));
      setAiLimits(new Map((await store.opAiLimits()).map((a) => [a.workshopId, a])));
      setSubs(new Map((await store.opSubscriptions()).map((x) => [x.workshopId, x])));
      setPricing(await store.opPricing());
    } catch (err) {
      setError(err);
    }
  }, [store]);
  useEffect(() => void load(), [load]);

  const tags = useMemo(() => [...new Set((rows ?? []).flatMap((r) => r.tags))].sort((a, b) => a.localeCompare(b, i18n.language)), [rows, i18n.language]);
  const open = rows?.find((r) => r.id === openId);
  if (open) return <OperatorWorkshop w={open} activity={activity.get(open.id)} ai={ai.get(open.id)} aiSettings={aiSettings} aiLimit={aiLimits.get(open.id)} sub={subs.get(open.id)} pricing={pricing} onChanged={load} onBack={() => go("operator")} />;

  const q = query.trim().toLowerCase();
  const shown = (rows ?? []).filter(
    (r) => (!q || r.name.toLowerCase().includes(q) || (r.ownerEmail ?? "").toLowerCase().includes(q) || r.tags.some((x) => x.toLowerCase().includes(q))) && (!tag || r.tags.includes(tag)),
  );
  const picked = (rows ?? []).filter((r) => selected.has(r.id));
  const toggle = (id: string, on: boolean) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const exportCsv = () => downloadCsv(workshopsCsv(picked, ai, activity, t), `garagevo-szervizek-${todayIso()}.csv`);

  return (
    <section className="operator">
      <nav className="op-bar" aria-label={t("operator.title")}>
        <button type="button" onClick={() => go("dashboard")}>← {t("operator.back")}</button>
        <strong>{t("operator.title")}</strong>
        <a href="#op-stats" onClick={(e) => { e.preventDefault(); document.getElementById("op-stats")?.scrollIntoView(); }}>{t("operator.nav.stats")}</a>
        <a href="#op-workshops" onClick={(e) => { e.preventDefault(); document.getElementById("op-workshops")?.scrollIntoView(); }}>{t("operator.nav.workshops")}</a>
        <a href="#op-ai-report" onClick={(e) => { e.preventDefault(); document.getElementById("op-ai-report")?.scrollIntoView(); }}>{t("operator.nav.aiReport")}</a>
        <a href="#op-ai" onClick={(e) => { e.preventDefault(); document.getElementById("op-ai")?.scrollIntoView(); }}>{t("operator.nav.ai")}</a>
        <a href="#op-pricing" onClick={(e) => { e.preventDefault(); document.getElementById("op-pricing")?.scrollIntoView(); }}>{t("operator.nav.pricing")}</a>
      </nav>
      <p className="muted small">{t("operator.intro")}</p>
      <ErrorText error={error} />
      <div id="op-stats" />
      {aiSettings && <OperatorStats alertShare={aiSettings.alertShare} onOpen={(id) => go("operator", id)} />}
      {rows && pricing && <SubscriptionSummary rows={rows} subs={subs} ai={ai} pricing={pricing} money={money} />}
      {rows && pricing && aiSettings && <AiReport rows={rows} subs={subs} pricing={pricing} alertShare={aiSettings.alertShare} onOpen={(id) => go("operator", id)} />}
      <h2 id="op-workshops">{t("operator.listTitle")}</h2>
      <div className="ops-filters">
        <input className="search ops-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("operator.search")} aria-label={t("operator.search")} />
        {tags.length > 0 && (
          <span className="ops-group" role="radiogroup" aria-label={t("operator.tags.filter")}>
            <span className="muted">{t("operator.tags.filter")}:</span>
            <label className={`ops-chip${tag === null ? " selected" : ""}`}>
              <input type="radio" name="op-tag" checked={tag === null} onChange={() => setTag(null)} />
              {t("operator.stats.all")}
            </label>
            {tags.map((x) => (
              <label key={x} className={`ops-chip${tag === x ? " selected" : ""}`}>
                <input type="radio" name="op-tag" checked={tag === x} onChange={() => setTag(x)} />
                {x}
              </label>
            ))}
          </span>
        )}
      </div>
      <div className="ops-selbar" aria-live="polite">
        <span>{t("operator.bulk.selected", { count: picked.length, shown: shown.length, total: rows?.length ?? 0 })}</span>
        <button type="button" disabled={shown.length === 0} onClick={() => setSelected(new Set([...selected, ...shown.map((r) => r.id)]))}>{t("operator.bulk.selectShown")}</button>
        <button type="button" disabled={picked.length === 0} onClick={() => setSelected(new Set())}>{t("operator.bulk.clear")}</button>
        <button type="button" disabled={picked.length === 0} onClick={exportCsv}>{t("operator.bulk.csv", { count: picked.length })}</button>
      </div>
      {picked.length > 0 && <BulkPanel rows={picked} tags={tags} onDone={load} />}
      {rows && shown.length === 0 && <p className="muted">{t("operator.none")}</p>}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table className="list">
            <thead>
              <tr>
                <th><span className="sr-only">{t("operator.bulk.tick")}</span></th>
                <th>{t("operator.col.workshop")}</th>
                <th>{t("operator.col.owner")}</th>
                <th>{t("operator.col.plan")}</th>
                <th>{t("operator.col.status")}</th>
                <th>{t("operator.col.paidUntil")}</th>
                <th>{t("operator.col.trial")}</th>
                <th>{t("operator.col.founder")}</th>
                <th>{t("operator.col.period")}</th>
                <th>{t("operator.col.invoice")}</th>
                <th>{t("operator.col.sms")}</th>
                <th>{t("operator.col.ai")}</th>
                <th>{t("operator.col.users")}</th>
                <th>{t("operator.col.workOrders")}</th>
                <th>{t("operator.col.lastActivity")}</th>
                <th>{t("operator.col.tags")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className={`clickable${selected.has(r.id) ? " selected" : ""}`} onClick={() => go("operator", r.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="op-tick" checked={selected.has(r.id)} onChange={(e) => toggle(r.id, e.target.checked)} aria-label={t("operator.bulk.tickRow", { name: r.name })} />
                  </td>
                  <td>
                    <a href={`#operator/${r.id}`} onClick={(e) => { e.preventDefault(); go("operator", r.id); }}>{r.name}</a>
                    {!r.termsAccepted && <div className="warn-text">{t("operator.termsMissing")}</div>}
                    {oddUse(ai.get(r.id), activity.get(r.id)) && <div className="warn-text">{t("operator.oddUse")}</div>}
                  </td>
                  <td>{r.ownerEmail ?? "–"}</td>
                  <td>{t(`plan.${r.plan}.label`)}</td>
                  <td><span className={`badge billing-${r.status}`}>{t(`billing.status.${r.status}`)}</span></td>
                  <td>{date(r.paidUntil)}</td>
                  <td>{trialCell(subs.get(r.id), t, date)}</td>
                  <td>{subs.get(r.id)?.founder ? <span title={date(subs.get(r.id)!.founderUntil)}>✓ {date(subs.get(r.id)!.founderUntil)}</span> : "–"}</td>
                  <td>{t(`subscription.periodShort.${subs.get(r.id)?.period ?? "monthly"}`)}{(subs.get(r.id)?.extraSeats ?? 0) > 0 && <> · +{subs.get(r.id)!.extraSeats}</>}</td>
                  <td className="num">{pricing ? money(invoiceOf(r, subs.get(r.id), ai.get(r.id), pricing).gross) : "–"}</td>
                  <td>{smsCell(r.smsCount, r.smsAmount, money, (n) => n.toLocaleString(i18n.language))}</td>
                  <td><AiCell u={ai.get(r.id)} fee={r.monthlyFee} alertShare={aiSettings?.alertShare ?? 0.3} /></td>
                  <td>{seatText(activity.get(r.id))}</td>
                  <td>{r.workOrderCount}</td>
                  <td>{date(r.lastActivity)}</td>
                  <td>{r.tags.map((x) => <span key={x} className="op-tag">{x}</span>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {aiSettings && <AiSettingsForm value={aiSettings} onSaved={load} />}
      {pricing && <PricingForm key={JSON.stringify(pricing)} value={pricing} onSaved={load} />}
    </section>
  );
}

type BulkKind = (typeof OP_BULK_ACTIONS)[number];

/**
 * What one bulk action would change in one workshop ("Alap → Profi"), or null when it changes
 * nothing there (already so, or the feature is not allowed in its package).
 */
export function bulkChange(w: OpWorkshop, a: OpBulkAction, t: TFunction, money: (minor: number) => string, date: (iso: string | null) => string, today = todayIso()): string | null {
  const onOff = (on: boolean) => t(on ? "operator.bulk.on" : "operator.bulk.off");
  switch (a.action) {
    case "plan":
      return w.plan === a.value.plan ? null : `${t(`plan.${w.plan}.label`)} → ${t(`plan.${a.value.plan}.label`)}`;
    case "feature": {
      const has = w.features.includes(a.value.key);
      if (has === a.value.on || (a.value.on && !featureAvailable(w.plan, a.value.key, w.featureGrants))) return null;
      return `${t(`feature.${a.value.key}.label`)}: ${onOff(has)} → ${onOff(a.value.on)}`;
    }
    case "status":
      return w.status === a.value.status ? null : `${t(`billing.status.${w.status}`)} → ${t(`billing.status.${a.value.status}`)}`;
    case "extend":
      return `${date(w.paidUntil)} → ${date(addMonths(w.paidUntil ?? today, a.value.months))}`;
    case "fee":
      return w.monthlyFee === a.value.monthlyFee ? null : `${w.monthlyFee === null ? "–" : money(w.monthlyFee)} → ${a.value.monthlyFee === null ? "–" : money(a.value.monthlyFee)}`;
    case "gift":
      return t("operator.bulk.giftLine", { amount: money(a.value.amount) });
    case "tagAdd":
      return w.tags.includes(a.value.tag.trim()) ? null : `+ ${a.value.tag.trim()}`;
    case "tagRemove":
      return w.tags.includes(a.value.tag.trim()) ? `− ${a.value.tag.trim()}` : null;
  }
}

/** Bulk actions on the ticked workshops: choose, preview "N workshops change like this", confirm in the page. */
function BulkPanel({ rows, tags, onDone }: { rows: OpWorkshop[]; tags: string[]; onDone: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const [kind, setKind] = useState<BulkKind>("plan");
  const [plan, setPlan] = useState<Plan>("medium");
  const [featureKey, setFeatureKey] = useState(FEATURES[0]!.key);
  const [featureOn, setFeatureOn] = useState(true);
  const [status, setStatus] = useState<BillingStatus>("active");
  const [fee, setFee] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [tag, setTag] = useState("");
  const [preview, setPreview] = useState<OpBulkAction | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const money = (minor: number) => formatMoney(minor, "HUF", i18n.language);
  const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : "–");
  const num = (v: string) => Number(v.replace(",", ".").trim());

  const missing: string[] = [];
  let action: OpBulkAction | null = null;
  if (kind === "plan") action = { action: "plan", value: { plan } };
  else if (kind === "feature") action = { action: "feature", value: { key: featureKey, on: featureOn } };
  else if (kind === "status") action = { action: "status", value: { status } };
  else if (kind === "extend") action = { action: "extend", value: { months: 1 } };
  else if (kind === "fee") {
    if (fee.trim() !== "" && !(num(fee) >= 0)) missing.push(t("operator.monthlyFee"));
    else action = { action: "fee", value: { monthlyFee: fee.trim() === "" ? null : toMinor(num(fee), "HUF") } };
  } else if (kind === "gift") {
    if (!(num(amount) > 0)) missing.push(t("operator.gift.amount"));
    if (!reason.trim()) missing.push(t("operator.gift.reason"));
    if (missing.length === 0) action = { action: "gift", value: { amount: Math.round(num(amount)), reason: reason.trim() } };
  } else {
    if (!tag.trim()) missing.push(t("operator.tags.tag"));
    else action = { action: kind, value: { tag: tag.trim() } };
  }

  const changes = preview ? rows.map((r) => ({ r, line: bulkChange(r, preview, t, money, date) })) : [];
  const changed = changes.filter((c) => c.line !== null);
  const same = changes.filter((c) => c.line === null);
  const pick = (k: BulkKind) => { setKind(k); setPreview(null); setDone(null); };
  const apply = async () => {
    if (!preview || changed.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await store.opBulk(changed.map((c) => c.r.id), preview);
      setDone(t("operator.bulk.done", { count: changed.length }));
      setPreview(null);
      await onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const chip = (name: string, checked: boolean, onChange: () => void, label: string) => (
    <label key={label} className={`ops-chip${checked ? " selected" : ""}`}>
      <input type="radio" name={name} checked={checked} onChange={onChange} />
      {label}
    </label>
  );

  return (
    <section className="ops-bulk" aria-label={t("operator.bulk.title")}>
      <h3>{t("operator.bulk.title")}</h3>
      <div className="ops-group" role="radiogroup" aria-label={t("operator.bulk.action")}>
        {OP_BULK_ACTIONS.map((k) => chip("bulk-action", kind === k, () => pick(k), t(`operator.bulk.kind.${k}`)))}
      </div>
      <div className="ops-bulk-value">
        {kind === "plan" && <div className="ops-group" role="radiogroup" aria-label={t("operator.plan")}>{PLANS.map((p) => chip("bulk-plan", plan === p, () => { setPlan(p); setPreview(null); }, t(`plan.${p}.label`)))}</div>}
        {kind === "feature" && (
          <>
            <div className="ops-group" role="radiogroup" aria-label={t("operator.modules")}>{FEATURES.map((f) => chip("bulk-feature", featureKey === f.key, () => { setFeatureKey(f.key); setPreview(null); }, t(`feature.${f.key}.label`)))}</div>
            <div className="ops-group" role="radiogroup" aria-label={t("operator.bulk.onOff")}>
              {chip("bulk-on", featureOn, () => { setFeatureOn(true); setPreview(null); }, t("operator.bulk.on"))}
              {chip("bulk-on", !featureOn, () => { setFeatureOn(false); setPreview(null); }, t("operator.bulk.off"))}
            </div>
          </>
        )}
        {kind === "status" && <div className="ops-group" role="radiogroup" aria-label={t("operator.col.status")}>{BILLING_STATUSES.map((s) => chip("bulk-status", status === s, () => { setStatus(s); setPreview(null); }, t(`billing.status.${s}`)))}</div>}
        {kind === "extend" && <p className="muted">{t("operator.bulk.extendNote")}</p>}
        {kind === "fee" && (
          <label className="field"><span>{t("operator.monthlyFee")}</span>
            <input inputMode="decimal" value={fee} onChange={(e) => { setFee(e.target.value); setPreview(null); }} aria-label={t("operator.bulk.feeValue")} /></label>
        )}
        {kind === "gift" && (
          <div className="form-grid">
            <label className="field"><span>{t("operator.gift.amount")}</span>
              <input inputMode="numeric" value={amount} onChange={(e) => { setAmount(e.target.value); setPreview(null); }} aria-label={t("operator.bulk.giftAmount")} /></label>
            <label className="field wide"><span>{t("operator.gift.reason")}</span>
              <input value={reason} onChange={(e) => { setReason(e.target.value); setPreview(null); }} aria-label={t("operator.bulk.giftReason")} /></label>
          </div>
        )}
        {(kind === "tagAdd" || kind === "tagRemove") && (
          <label className="field"><span>{t("operator.tags.tag")}</span>
            <input list="op-bulk-tags" value={tag} onChange={(e) => { setTag(e.target.value); setPreview(null); }} aria-label={t("operator.bulk.tagValue")} maxLength={30} />
            <datalist id="op-bulk-tags">{[...new Set([...tags, ...SUGGESTED_TAGS])].map((x) => <option key={x} value={x} />)}</datalist></label>
        )}
      </div>
      <div className="actions">
        <button type="button" disabled={!action} onClick={() => { setDone(null); setPreview(action); }}>{t("operator.bulk.preview", { count: rows.length })}</button>
        {missing.length > 0 && <span className="missing">{t("operator.missing", { fields: missing.join(", ") })}</span>}
      </div>
      <ErrorText error={error} />
      {done && <p className="ok">{done}</p>}
      {preview && (
        <div className="ops-confirm" role="region" aria-label={t("operator.bulk.confirmTitle")}>
          <strong>{t("operator.bulk.willChange", { count: changed.length })}</strong>
          {changed.length > 0 && <ul>{changed.map((c) => <li key={c.r.id}><b>{c.r.name}</b>: {c.line}</li>)}</ul>}
          {same.length > 0 && <p className="muted">{t("operator.bulk.unchanged", { count: same.length, names: same.map((c) => c.r.name).join(", ") })}</p>}
          <div className="actions">
            <button type="button" className="primary" disabled={busy || changed.length === 0} onClick={() => void apply()}>{t("operator.bulk.apply", { count: changed.length })}</button>
            <button type="button" onClick={() => setPreview(null)}>{t("operator.bulk.cancel")}</button>
          </div>
        </div>
      )}
    </section>
  );
}

/** CSV of the chosen workshops (semicolon separated, for Excel), in the page's language. */
export function workshopsCsv(rows: OpWorkshop[], ai: Map<string, OpAiUsage>, activity: Map<string, OpActivity>, t: TFunction): string {
  const cell = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = [
    "operator.col.workshop", "operator.col.owner", "operator.col.plan", "operator.col.status", "operator.monthlyFee", "operator.discount",
    "operator.col.paidUntil", "operator.col.tags", "operator.col.users", "operator.col.workOrders", "operator.col.lastActivity",
    "operator.csv.aiCalls", "operator.csv.aiCost", "operator.csv.aiCharged", "operator.csv.gift", "operator.note",
  ].map((k) => t(k));
  const lines = rows.map((r) => {
    const u = ai.get(r.id);
    return [
      r.name, r.ownerEmail, t(`plan.${r.plan}.label`), t(`billing.status.${r.status}`), r.monthlyFee === null ? "" : fromMinor(r.monthlyFee, "HUF"),
      r.discountPct ?? "", r.paidUntil, r.tags.join(", "), seatText(activity.get(r.id)), r.workOrderCount, r.lastActivity?.slice(0, 10),
      u?.calls ?? 0, u?.costHuf ?? 0, u?.chargedHuf ?? 0, r.giftMonthHuf, r.note,
    ].map(cell).join(";");
  });
  return [head.map(cell).join(";"), ...lines].join("\r\n") + "\r\n";
}

function downloadCsv(csv: string, name: string) {
  const url = URL.createObjectURL(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function AiCell({ u, fee, alertShare }: { u: OpAiUsage | undefined; fee: number | null; alertShare: number }) {
  const { t, i18n } = useTranslation();
  const money = (huf: number) => formatMoney(huf, "HUF", i18n.language);
  if (!u || u.calls === 0) return <>–</>;
  const alert = aiCostAlert(u, fee, alertShare);
  return (
    <span className={alert ? "warn-text" : undefined} title={alert ? t("operator.ai.alert") : undefined}>
      {t("operator.ai.cell", { calls: u.calls, cost: money(u.costHuf), charged: money(u.chargedHuf) })}
    </span>
  );
}

/** Platform wide AI rules: markup, exchange rate, monthly allowance per package, extra packs, alert share. */
function AiSettingsForm({ value, onSaved }: { value: AiSettings; onSaved: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const [s, setS] = useState(() => ({
    markup: String(value.markup),
    vatRate: String(value.vatRate ?? 27),
    usdHuf: String(value.usdHuf),
    basic: String(value.allowance.basic),
    medium: String(value.allowance.medium),
    premium: String(value.allowance.premium),
    packs: value.packs.join(" "),
    alertShare: String(Math.round(value.alertShare * 100)),
    freeKinds: value.freeKinds.join(" "),
    freeBasic: String(value.freeCalls?.basic ?? AI_GUARD_DEFAULTS.freeCalls.basic),
    freeMedium: String(value.freeCalls?.medium ?? AI_GUARD_DEFAULTS.freeCalls.medium),
    freePremium: String(value.freeCalls?.premium ?? AI_GUARD_DEFAULTS.freeCalls.premium),
    dailyCostCapHuf: String(value.dailyCostCapHuf ?? AI_GUARD_DEFAULTS.dailyCostCapHuf),
    workshopDailyCapHuf: String(value.workshopDailyCapHuf ?? AI_GUARD_DEFAULTS.workshopDailyCapHuf),
    trialAllowance: String(value.trialAllowance ?? AI_GUARD_DEFAULTS.trialAllowance),
    trialFreeCalls: String(value.trialFreeCalls ?? AI_GUARD_DEFAULTS.trialFreeCalls),
    trialSheetFills: String(value.trialSheetFills ?? AI_GUARD_DEFAULTS.trialSheetFills),
    rateLimitPerMinute: String(value.rateLimitPerMinute ?? AI_GUARD_DEFAULTS.rateLimitPerMinute),
    rateLimitPerDay: String(value.rateLimitPerDay ?? AI_GUARD_DEFAULTS.rateLimitPerDay),
    monthlyPurchaseLimitHuf: String(value.monthlyPurchaseLimitHuf ?? AI_GUARD_DEFAULTS.monthlyPurchaseLimitHuf),
  }));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const num = (v: string) => Number(v.replace(",", ".").trim());
  const set = (k: keyof typeof s) => (e: ChangeEvent<HTMLInputElement>) => { setSaved(false); setS({ ...s, [k]: e.target.value }); };
  const save = async () => {
    setError(null);
    try {
      await store.opSetAiSettings({
        markup: num(s.markup),
        vatRate: num(s.vatRate),
        usdHuf: num(s.usdHuf),
        allowance: { basic: num(s.basic), medium: num(s.medium), premium: num(s.premium) },
        packs: s.packs.split(/[\s;]+/).filter(Boolean).map(num),
        alertShare: num(s.alertShare) / 100,
        freeKinds: s.freeKinds.split(/[\s;,]+/).filter(Boolean),
        // the old flat number stays for an older database; the plans' numbers come first
        freeMonthlyCalls: num(s.freePremium),
        freeCalls: { basic: num(s.freeBasic), medium: num(s.freeMedium), premium: num(s.freePremium) },
        dailyCostCapHuf: num(s.dailyCostCapHuf),
        workshopDailyCapHuf: num(s.workshopDailyCapHuf),
        trialAllowance: num(s.trialAllowance),
        trialFreeCalls: num(s.trialFreeCalls),
        trialSheetFills: num(s.trialSheetFills),
        rateLimitPerMinute: num(s.rateLimitPerMinute),
        rateLimitPerDay: num(s.rateLimitPerDay),
        monthlyPurchaseLimitHuf: num(s.monthlyPurchaseLimitHuf),
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err);
    }
  };
  const field = (k: keyof typeof s, label: string, wide = false) => (
    <label className={`field${wide ? " wide" : ""}`}>
      <span>{label}</span>
      <input id={`ai-${k}`} inputMode={k === "freeKinds" ? "text" : "decimal"} value={s[k]} onChange={set(k)} aria-label={label} />
    </label>
  );
  return (
    <section className="operator-ai" id="op-ai">
      <h2>{t("operator.ai.title")}</h2>
      <p className="muted">{t("operator.ai.intro")}</p>
      <ErrorText error={error} />
      {saved && <p className="ok">{t("operator.saved")}</p>}
      <div className="form-grid">
        {field("markup", t("operator.ai.markup"))}
        {field("vatRate", t("operator.ai.vatRate"))}
        {field("usdHuf", t("operator.ai.usdHuf"))}
        <p className={usdHufStale(value.usdHufChangedAt, todayIso()) ? "warn-text small" : "muted small"} role={usdHufStale(value.usdHufChangedAt, todayIso()) ? "alert" : undefined}>
          {value.usdHufChangedAt ? t("operator.ai.usdHufChanged", { date: new Date(value.usdHufChangedAt).toLocaleDateString(i18n.language) }) : t("operator.ai.usdHufUnknown")}
          {usdHufStale(value.usdHufChangedAt, todayIso()) && <> {t("operator.ai.usdHufStale", { days: USD_HUF_STALE_DAYS })}</>}
        </p>
        {field("alertShare", t("operator.ai.alertShare"))}
        {field("basic", t("operator.ai.allowance", { plan: t("plan.basic.label") }))}
        {field("medium", t("operator.ai.allowance", { plan: t("plan.medium.label") }))}
        {field("premium", t("operator.ai.allowance", { plan: t("plan.premium.label") }))}
        {field("packs", t("operator.ai.packs"), true)}
        {field("freeBasic", t("operator.ai.freeCalls", { plan: t("plan.basic.label") }))}
        {field("freeMedium", t("operator.ai.freeCalls", { plan: t("plan.medium.label") }))}
        {field("freePremium", t("operator.ai.freeCalls", { plan: t("plan.premium.label") }))}
        {field("freeKinds", t("operator.ai.freeKinds"), true)}
      </div>
      <h3>{t("operator.ai.guardTitle")}</h3>
      <p className="muted small">{t("operator.ai.guardIntro")}</p>
      <div className="form-grid">
        {field("dailyCostCapHuf", t("operator.ai.dailyCostCap"))}
        {field("workshopDailyCapHuf", t("operator.ai.workshopDailyCap"))}
        {field("rateLimitPerMinute", t("operator.ai.rateMinute"))}
        {field("rateLimitPerDay", t("operator.ai.rateDay"))}
        {field("monthlyPurchaseLimitHuf", t("operator.ai.purchaseLimit"))}
        {field("trialAllowance", t("operator.ai.trialAllowance"))}
        {field("trialFreeCalls", t("operator.ai.trialFreeCalls"))}
        {field("trialSheetFills", t("operator.ai.trialSheetFills"))}
      </div>
      <div className="actions">
        <button type="button" className="primary" onClick={() => void save()}>{t("operator.save")}</button>
      </div>
    </section>
  );
}

/** "until 12.01. (5 days)" while the trial runs, the chosen plan after it, or "–". */
function trialCell(s: OpSubscription | undefined, t: TFunction, date: (iso: string | null) => string) {
  if (!s?.trialUntil) return "–";
  const today = todayIso();
  if (s.readOnly) return <span className="warn-text">{t("operator.sub.readOnly", { date: date(s.trialUntil) })}</span>;
  if (!inTrial(s, today)) return <span className="muted">{t("operator.sub.trialEnded", { date: date(s.trialUntil) })}</span>;
  const days = daysBetween(today, s.trialUntil) + 1;
  return (
    <span className={days <= 7 ? "warn-text" : undefined}>
      {t("operator.sub.trialLeft", { date: date(s.trialUntil), days })}
      <br />
      <small className="muted">{s.planAfterTrial ? t(`plan.${s.planAfterTrial}.label`) : t("operator.sub.notChosen")}</small>
    </span>
  );
}

/** Totals of the subscriptions this month (stats strip above the list). */
function SubscriptionSummary({ rows, subs, ai, pricing, money }: { rows: OpWorkshop[]; subs: Map<string, OpSubscription>; ai: Map<string, OpAiUsage>; pricing: Pricing; money: (n: number) => string }) {
  const { t } = useTranslation();
  const x = subscriptionTotals(rows, subs, ai, pricing);
  return (
    <div className="opw-strip op-sub-totals" role="group" aria-label={t("operator.sub.totals")}>
      <div className="opw-fact"><span className="opw-fact-label">{t("operator.sub.invoiceMonth")}</span><span className="opw-fact-value">{t("operator.sub.amount", { net: money(x.net), gross: money(x.gross) })}</span></div>
      <div className="opw-fact"><span className="opw-fact-label">{t("operator.sub.invoices")}</span><span className="opw-fact-value">{x.invoices}</span></div>
      <div className="opw-fact"><span className="opw-fact-label">{t("operator.sub.inTrial")}</span><span className="opw-fact-value">{x.trial}</span></div>
      <div className="opw-fact"><span className="opw-fact-label">{t("operator.sub.founders")}</span><span className="opw-fact-value">{x.founders} / {pricing.founder.limit}</span></div>
      <div className="opw-fact"><span className="opw-fact-label">{t("operator.sub.yearly")}</span><span className="opw-fact-value">{x.yearly}</span></div>
    </div>
  );
}

/** The price list: plan prices, extra user, VAT, yearly free months, trial days and its Profi week, founder offer. Logged. */
function PricingForm({ value, onSaved }: { value: Pricing; onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [s, setS] = useState(() => ({
    basic: String(value.plans.basic), medium: String(value.plans.medium), premium: String(value.plans.premium),
    extraUserNet: String(value.extraUserNet), vatRate: String(value.vatRate), yearlyFreeMonths: String(value.yearlyFreeMonths),
    trialDays: String(value.trialDays), trialUpgradeDays: String(value.trialUpgradeDays), trialUpgradePlan: value.trialUpgradePlan as string, limit: String(value.founder.limit), months: String(value.founder.months),
    percent: String(value.founder.percent), lockMonths: String(value.founder.lockMonths), founderTrialDays: String(value.founder.trialDays),
  }));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const n = (v: string) => Number(v.trim());
  const next = parsePricing({
    plans: { basic: n(s.basic), medium: n(s.medium), premium: n(s.premium) },
    extraUserNet: n(s.extraUserNet), vatRate: n(s.vatRate), yearlyFreeMonths: n(s.yearlyFreeMonths), trialDays: n(s.trialDays),
    trialUpgradeDays: n(s.trialUpgradeDays), trialUpgradePlan: s.trialUpgradePlan,
    founder: { limit: n(s.limit), months: n(s.months), percent: n(s.percent), lockMonths: n(s.lockMonths), trialDays: n(s.founderTrialDays) },
  });
  const save = async () => {
    if (!next) return;
    setError(null);
    try {
      await store.opSetPricing(next);
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err);
    }
  };
  const field = (k: keyof typeof s, label: string) => (
    <label className="field">
      <span>{label}</span>
      <input inputMode="numeric" value={s[k]} onChange={(e) => { setSaved(false); setS({ ...s, [k]: e.target.value }); }} aria-label={label} />
    </label>
  );
  return (
    <section className="operator-ai" id="op-pricing">
      <h2>{t("operator.pricing.title")}</h2>
      <p className="muted">{t("operator.pricing.intro")}</p>
      <ErrorText error={error} />
      {saved && <p className="ok">{t("operator.saved")}</p>}
      <div className="form-grid">
        {PLANS.map((p) => <span key={p}>{field(p, t("operator.pricing.plan", { plan: t(`plan.${p}.label`) }))}</span>)}
        {field("extraUserNet", t("operator.pricing.extraUser"))}
        {field("vatRate", t("operator.pricing.vatRate"))}
        {field("yearlyFreeMonths", t("operator.pricing.yearlyFree"))}
        {field("trialDays", t("operator.pricing.trialDays"))}
        {field("trialUpgradeDays", t("operator.pricing.trialUpgradeDays"))}
        <fieldset className="choice-cards">
          <legend>{t("operator.pricing.trialUpgradePlan")}</legend>
          {PLANS.map((p) => (
            <label key={p} className={`choice-card${s.trialUpgradePlan === p ? " selected" : ""}`}>
              <input type="radio" name="trialUpgradePlan" checked={s.trialUpgradePlan === p} onChange={() => { setSaved(false); setS({ ...s, trialUpgradePlan: p }); }} />
              <span>{t(`plan.${p}.label`)}</span>
            </label>
          ))}
        </fieldset>
        {field("limit", t("operator.pricing.founderLimit"))}
        {field("months", t("operator.pricing.founderMonths"))}
        {field("percent", t("operator.pricing.founderPercent"))}
        {field("lockMonths", t("operator.pricing.founderLock"))}
        {field("founderTrialDays", t("operator.pricing.founderTrialDays"))}
      </div>
      <div className="actions">
        <button type="button" className="primary" disabled={!next} onClick={() => void save()}>{t("operator.save")}</button>
        {!next && <span className="missing">{t("operator.pricing.invalid")}</span>}
      </div>
    </section>
  );
}
